/**
 * SecurityPage — /guvenlik
 *
 * Where a business's customer data lives, what reaches the AI, how it is
 * protected and deleted, and Hümo on the customer's own server. Every claim is
 * something the product does today: storage in AWS Frankfurt and inference in
 * the US (apps/backend/docs/DATA_RESIDENCY.md, Anthropic API since 17.10),
 * AES-256-GCM for channel credentials (utils/crypto.ts), Meta signature checks,
 * customer deletion and retention (#116), on-prem (#120, docs/onprem.md).
 * No certifications, no backup promises for the cloud: neither exists in
 * writing. Change this page when any of that changes.
 */

import { useState } from "react";
import {
  Building2,
  Check,
  ChevronRight,
  Cloud,
  Cpu,
  CreditCard,
  FileCheck,
  Fingerprint,
  Globe,
  KeyRound,
  Lock,
  Server,
  ShieldCheck,
  Timer,
  Trash2,
  Webhook,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { getMarketingCopy } from "../content/marketingCopy";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { Button } from "../components/ui/button";
import { SECURITY_FAQ } from "../content/securityFaq";
import { trackMetaEvent } from "../lib/metaPixel";

type L = { tr: string; en: string };
type Setup = "cloud" | "onprem";

/** One row of the "where is your data" card, in each setup. */
const DATA_MAP: { what: L; cloud: L; onprem: L }[] = [
  {
    what: { tr: "Konuşmalar, müşteri kartları, bilgi bankası, dosyalar", en: "Conversations, customer cards, knowledge base, files" },
    cloud: { tr: "AWS Frankfurt, Almanya", en: "AWS Frankfurt, Germany" },
    onprem: { tr: "Sizin sunucunuz", en: "Your server" },
  },
  {
    what: { tr: "Yapay zekânın cevap yazması", en: "The AI writing replies" },
    cloud: { tr: "Anthropic, yedek AWS Bedrock (ABD)", en: "Anthropic, AWS Bedrock as backup (US)" },
    onprem: {
      tr: "Seçiminiz: kendi Anthropic ya da AWS hesabınız, veya sunucunuzda yerel model",
      en: "Your choice: your own Anthropic or AWS account, or a local model on your server",
    },
  },
  {
    what: { tr: "Sistem e-postaları (şifre sıfırlama, davet)", en: "System emails (password reset, invites)" },
    cloud: { tr: "Amazon SES", en: "Amazon SES" },
    onprem: { tr: "Sizin e-posta sunucunuz", en: "Your mail server" },
  },
  {
    what: { tr: "Ekibe giden anlık bildirimler", en: "Push notifications to your team" },
    cloud: { tr: "Google Firebase", en: "Google Firebase" },
    onprem: { tr: "Yalnız uygulamanın içinde", en: "Inside the app only" },
  },
  {
    what: { tr: "Kart bilgileri", en: "Card details" },
    cloud: { tr: "Hümo'ya gelmez: Paratika, 3D Secure", en: "Never reach Hümo: Paratika, 3D Secure" },
    onprem: { tr: "Ödeme yok, sözleşmeyle", en: "No payments, by contract" },
  },
];

const PROTECTIONS: { icon: React.ElementType; title: L; body: L }[] = [
  {
    icon: Lock,
    title: { tr: "Her bağlantı şifreli", en: "Every connection encrypted" },
    body: {
      tr: "Uygulama, API ve sitenizdeki sohbet balonu yalnız HTTPS üzerinden çalışır.",
      en: "The app, the API and the chat bubble on your site work over HTTPS only.",
    },
  },
  {
    icon: KeyRound,
    title: { tr: "Bağlantı anahtarları şifreli", en: "Channel keys encrypted" },
    body: {
      tr: "Instagram, WhatsApp ve e-posta bağlantılarınızın erişim anahtarları veritabanında AES-256-GCM ile şifreli durur.",
      en: "The access keys for your Instagram, WhatsApp and email connections are stored encrypted with AES-256-GCM.",
    },
  },
  {
    icon: Fingerprint,
    title: { tr: "Şifreler geri çözülemez", en: "Passwords can't be read back" },
    body: {
      tr: "Kullanıcı şifreleri bcrypt ile saklanır; düz hâlini biz de göremeyiz.",
      en: "User passwords are stored with bcrypt; not even we can see them.",
    },
  },
  {
    icon: Building2,
    title: { tr: "İşletmeler birbirinden ayrı", en: "Businesses kept apart" },
    body: {
      tr: "Her kayıt işletmenize bağlıdır; bir işletme başka birinin konuşmasını göremez. Ekibinizde yönetici ve temsilci rolleri ayrıdır.",
      en: "Every record belongs to your business; no business can see another's conversations. Admin and agent roles are separate.",
    },
  },
  {
    icon: Webhook,
    title: { tr: "Sahte mesaj giremez", en: "No forged messages" },
    body: {
      tr: "Instagram ve WhatsApp'tan gelen her bildirim Meta'nın imzasıyla doğrulanır; imzası tutmayan istek reddedilir.",
      en: "Every notification from Instagram and WhatsApp is checked against Meta's signature; anything that doesn't match is rejected.",
    },
  },
  {
    icon: Globe,
    title: { tr: "Sohbet balonu yalnız sizin sitenizde", en: "The chat bubble on your site only" },
    body: {
      tr: "Widget anahtarınızı hangi alan adlarının kullanabileceğini siz belirlersiniz.",
      en: "You decide which domains may use your widget key.",
    },
  },
  {
    icon: ShieldCheck,
    title: { tr: "AI'ın kuralları müşteriye kapalı", en: "The AI's rules stay closed" },
    body: {
      tr: "Müşteri mesajı AI'a ayrı ve işaretli bir alan olarak verilir; içindeki talimatlar kuralların yerine geçmesin diye. Bilgi bankanızda karşılığı olmayan fiyat, saat ya da rakam içeren cevap gönderilmeden yakalanır.",
      en: "Customer messages reach the AI in a separate, marked field, so instructions inside them don't replace its rules. A reply with a price, time or figure your knowledge base doesn't back is caught before it is sent.",
    },
  },
  {
    icon: CreditCard,
    title: { tr: "Kart bilgisi bize gelmez", en: "Card details never reach us" },
    body: {
      tr: "Abonelik ödemesi Paratika'nın sayfasında, bankanızın 3D Secure onayıyla alınır.",
      en: "Subscriptions are paid on Paratika's page, with your bank's 3D Secure check.",
    },
  },
];

const KVKK: { icon: React.ElementType; title: L; body: L }[] = [
  {
    icon: Trash2,
    title: { tr: "Bir müşterinin verisini silin", en: "Delete one customer's data" },
    body: {
      tr: "Müşteri kartından tek işlemle: o kişinin bütün konuşmaları, mesajları, dosyaları ve AI'ın onun hakkındaki notları silinir. Randevu ve talep kayıtlarınız kalır, içindeki kişisel bilgiler çıkarılır.",
      en: "In one step from the customer card: all of that person's conversations, messages, files and the AI's notes on them are deleted. Your bookings and requests stay, with personal details removed.",
    },
  },
  {
    icon: Timer,
    title: { tr: "Saklama süresini siz seçin", en: "Choose how long to keep data" },
    body: {
      tr: "6, 12, 24 ya da 36 ay. Süresi dolan konuşmalar her gece kendiliğinden silinir. Siz seçene kadar hiçbir şey silinmez.",
      en: "6, 12, 24 or 36 months. Conversations past that are deleted every night. Nothing is deleted until you choose.",
    },
  },
  {
    icon: FileCheck,
    title: { tr: "Her silme kayıt altında", en: "Every deletion on record" },
    body: {
      tr: "Ne zaman, kim, neden ve kaç kayıt silindiği, kişisel veri içermeyen bir imha kaydında tutulur.",
      en: "When, by whom, why and how many records were deleted is kept in a destruction log that holds no personal data.",
    },
  },
];

const AI_OPTIONS: { icon: React.ElementType; title: L; body: L; tag?: L }[] = [
  {
    icon: KeyRound,
    title: { tr: "Kendi Anthropic hesabınız", en: "Your own Anthropic account" },
    body: {
      tr: "Bulutumuzdaki modelin aynısı (Claude). Mesajın gereken kısmı Anthropic'e gider; anahtar ve fatura sizin.",
      en: "The same model as our cloud (Claude). Only what a reply needs goes to Anthropic; the key and the bill are yours.",
    },
    tag: { tr: "Önerilen", en: "Recommended" },
  },
  {
    icon: Cloud,
    title: { tr: "Kendi AWS hesabınız", en: "Your own AWS account" },
    body: {
      tr: "Claude, sizin AWS hesabınızda ve seçtiğiniz bölgede (ör. Frankfurt) çalışır. Sözleşmenin tarafı sizsiniz.",
      en: "Claude runs in your AWS account, in the region you choose (e.g. Frankfurt). You are the party to the contract.",
    },
  },
  {
    icon: Cpu,
    title: { tr: "Tamamen yerel model", en: "Fully local model" },
    body: {
      tr: "Açık kaynak bir model sizin GPU'lu sunucunuzda çalışır; mesajlar binadan çıkmaz. Cevap kalitesi Claude'un gerisinde kalır, kararı birlikte deneyerek veririz.",
      en: "An open model runs on your GPU server; messages never leave the building. Reply quality trails Claude, so we decide together after a trial.",
    },
  },
];

const ONPREM_INCLUDES: L[] = [
  { tr: "Docker ile tek komutla kurulum: veritabanı, kuyruk, uygulama ve HTTPS birlikte gelir", en: "One-command Docker install: database, queue, app and HTTPS together" },
  { tr: "Kendi alan adınız, sertifikanız ve e-posta sunucunuz", en: "Your own domain, certificate and mail server" },
  { tr: "Bize hiçbir veri gelmez: telemetri, analitik ya da uzaktan erişim yok", en: "Nothing comes to us: no telemetry, analytics or remote access" },
  { tr: "Yedekleme, tek komutla geri yükleme ve güncelleme", en: "Backups, one-command restore and updates" },
  { tr: "İnternete kapalı ağlara kurulum", en: "Installs on networks closed to the internet" },
  { tr: "Ödeme ekranı yok; plan sözleşmeyle tanımlı", en: "No payment screens; the plan is set by contract" },
];

const mailto = (address: string, subject: string) =>
  `mailto:${address}?subject=${encodeURIComponent(subject)}`;

const SecurityPage = () => {
  const { language, setLanguage } = useLanguage();
  const isTr = language === "tr";
  const lang: "tr" | "en" = isTr ? "tr" : "en";
  const copy = getMarketingCopy(language);
  const [setup, setSetup] = useState<Setup>("cloud");

  const onPremInquiry = () => {
    trackMetaEvent("Lead", { content_name: "onprem_inquiry" });
    window.open(mailto("info@meethumo.com", isTr ? "Kendi sunucumuzda Hümo" : "Hümo on our own server"), "_blank", "noopener");
  };

  return (
    <MarketingLayout copy={copy} language={language} setLanguage={setLanguage}>
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16">
        {/* Hero */}
        <div className="mb-16 grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{isTr ? "Güvenlik ve KVKK" : "Security and KVKK"}</span>
            </div>
            <h1 className="text-4xl font-bold leading-tight sm:text-5xl">
              {isTr ? "Müşterilerinizin verisi nerede, kim görür, ne zaman silinir?" : "Where your customers' data lives, who sees it, when it's deleted"}
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              {isTr
                ? "Hümo'ya gelen her mesaj sizin müşterinizin verisi. Nerede saklandığını, yapay zekâya neyin gittiğini ve nasıl silindiğini burada açıkça yazıyoruz. Verinin kurumunuzdan çıkmaması gerekiyorsa Hümo'yu kendi sunucunuza kuruyoruz."
                : "Every message that reaches Hümo is your customer's data. Here we spell out where it is stored, what goes to the AI and how it is deleted. If data must not leave your organisation, we install Hümo on your own server."}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button size="lg" asChild>
                <a href="#kendi-sunucunuz">
                  {isTr ? "Kendi sunucunuzda Hümo" : "Hümo on your own server"}
                  <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href={mailto("kvkk@meethumo.com", isTr ? "Güvenlik ve KVKK sorusu" : "Security and KVKK question")}>
                  {isTr ? "Sorunuzu yazın" : "Ask us"}
                </a>
              </Button>
            </div>
          </div>

          {/* Where the data lives, in each setup */}
          <div className="rounded-2xl border border-border bg-card p-5 shadow-xl">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
              <h2 className="text-base font-semibold">{isTr ? "Veriniz nerede" : "Where your data lives"}</h2>
              <div role="tablist" aria-label={isTr ? "Kurulum" : "Setup"} className="inline-flex rounded-lg bg-muted p-1 text-xs font-medium">
                {(["cloud", "onprem"] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="tab"
                    aria-selected={setup === option}
                    onClick={() => setSetup(option)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 transition-colors ${
                      setup === option ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {option === "cloud" ? <Cloud className="h-3.5 w-3.5" aria-hidden="true" /> : <Server className="h-3.5 w-3.5" aria-hidden="true" />}
                    {option === "cloud" ? (isTr ? "Hümo bulutu" : "Hümo cloud") : isTr ? "Kendi sunucunuzda" : "Your own server"}
                  </button>
                ))}
              </div>
            </div>
            <dl className="divide-y divide-border">
              {DATA_MAP.map((row) => (
                <div key={row.what.en} className="grid gap-1 py-3 sm:grid-cols-[1fr_1.1fr] sm:gap-4">
                  <dt className="text-sm text-muted-foreground">{row.what[lang]}</dt>
                  <dd className="text-sm font-medium">{row[setup][lang]}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-[11px] text-muted-foreground">
              {setup === "cloud"
                ? isTr
                  ? "Bulut kurulumumuz, Ekim 2026. Instagram, WhatsApp ve Telegram mesajları ayrıca o platformlarda durur."
                  : "Our cloud setup, October 2026. Instagram, WhatsApp and Telegram messages also stay on those platforms."
                : isTr
                  ? "Sunucunuzdan dışarı yalnız sizin seçtiğiniz bağlantılar çıkar (yapay zekâ hesabınız, kanallarınız)."
                  : "Only the connections you choose leave your server (your AI account, your channels)."}
            </p>
          </div>
        </div>

        {/* How it is protected */}
        <section className="mb-16" aria-labelledby="protect-heading">
          <div className="mb-8 text-center">
            <h2 id="protect-heading" className="text-3xl font-bold">
              {isTr ? "Nasıl korunuyor" : "How it is protected"}
            </h2>
            <p className="mt-2 text-muted-foreground">
              {isTr ? "Sözle değil, bugün sistemde çalışan önlemler." : "Not promises: measures running in the product today."}
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PROTECTIONS.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title.en} className="rounded-2xl border border-border bg-card p-5">
                  <div className="mb-3 inline-flex rounded-xl bg-primary/10 p-2">
                    <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="font-semibold">{item.title[lang]}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body[lang]}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* KVKK */}
        <section className="mb-16" aria-labelledby="kvkk-heading">
          <div className="mb-8 text-center">
            <h2 id="kvkk-heading" className="text-3xl font-bold">
              {isTr ? "KVKK: kontrol sizde" : "KVKK: you are in control"}
            </h2>
            <p className="mt-2 text-muted-foreground">
              {isTr
                ? "Silme talebine 30 gün içinde cevap vermek ve veriyi süresi dolunca imha etmek için."
                : "To answer a deletion request within 30 days and destroy data when its time is up."}
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {KVKK.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title.en} className="rounded-2xl border border-border bg-card p-6">
                  <div className="mb-3 inline-flex rounded-xl bg-emerald-500/10 p-2">
                    <Icon className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                  </div>
                  <h3 className="font-semibold">{item.title[lang]}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body[lang]}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* On-prem */}
        <section
          id="kendi-sunucunuz"
          className="mb-16 scroll-mt-24 rounded-2xl bg-gradient-to-br from-slate-500/10 to-sky-500/10 p-8 sm:p-10"
          aria-labelledby="onprem-heading"
        >
          <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-background/70 px-3 py-1 text-xs font-medium">
                <Server className="h-3.5 w-3.5" aria-hidden="true" />
                <span>{isTr ? "Kurumsal" : "Enterprise"}</span>
              </div>
              <h2 id="onprem-heading" className="text-2xl font-bold sm:text-3xl">
                {isTr ? "Kendi sunucunuzda Hümo" : "Hümo on your own server"}
              </h2>
              <p className="mt-3 text-muted-foreground">
                {isTr
                  ? "Belediyeler, sağlık kurumları ve verisini dışarı çıkaramayan şirketler için. Uygulama aynı; konuşmalar, müşteri kartları, bilgi bankası ve dosyalar sizin veritabanınızda ve diskinizde kalır."
                  : "For municipalities, healthcare and companies whose data cannot leave the building. Same app; conversations, customer cards, knowledge base and files stay in your database and on your disks."}
              </p>
              <ul className="mt-6 space-y-2.5 text-sm">
                {ONPREM_INCLUDES.map((item) => (
                  <li key={item.en} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    <span>{item[lang]}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7">
                <Button onClick={onPremInquiry}>{isTr ? "Kurulum için görüşelim" : "Talk to us about installing"}</Button>
              </div>
            </div>

            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {isTr ? "Yapay zekâyı siz seçersiniz" : "You choose the AI"}
              </h3>
              <div className="space-y-3">
                {AI_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <div key={option.title.en} className="rounded-xl border border-border bg-card p-4">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
                        <h4 className="font-semibold">{option.title[lang]}</h4>
                        {option.tag && (
                          <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                            {option.tag[lang]}
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{option.body[lang]}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="mb-6 text-center text-3xl font-bold">
            {isTr ? "Sık sorulanlar" : "Questions"}
          </h2>
          <div className="mx-auto max-w-3xl divide-y divide-border rounded-2xl border border-border bg-card">
            {SECURITY_FAQ.map((item, i) => (
              <details key={i} className="group p-5">
                <summary className="cursor-pointer list-none font-medium marker:hidden">
                  <span className="flex items-center justify-between gap-4">
                    {item.q[lang]}
                    <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.a[lang]}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="rounded-2xl bg-card p-8 text-center shadow-md sm:p-10">
          <h2 className="text-2xl font-bold sm:text-3xl">
            {isTr ? "Güvenlik sorunuz mu var?" : "A security question?"}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            {isTr
              ? "Kurumunuzun güvenlik ya da KVKK formunu gönderin, maddeleri tek tek cevaplayalım."
              : "Send us your organisation's security or KVKK questionnaire and we'll answer it item by item."}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button asChild>
              <a href={mailto("kvkk@meethumo.com", isTr ? "Güvenlik ve KVKK sorusu" : "Security and KVKK question")}>kvkk@meethumo.com</a>
            </Button>
            <Button variant="outline" onClick={onPremInquiry}>
              {isTr ? "Kendi sunucumuzda kurulum" : "Install on our own server"}
            </Button>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default SecurityPage;
